import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import {
  CanadianWorkersCompensation,
  CompanyVendorFile,
  Owner,
  OwnerFinding
} from '../classes/models';
import { BusinessComplianceSetting } from '../classes/models/business-compliance-setting';
import { ComplianceFinding } from '../classes/models/compliance-finding';
import { ComplianceSetting } from '../classes/models/compliance-setting';
import { Note } from '../classes/models/note';
import { ComplianceResultStatuses } from '../interfaces/compliance/compliance-result-statuses';
import { AUTOInsuranceComplianceResults } from '../interfaces/compliance/results/insurance/auto-insurance-compliance-results';
import { GLInsuranceComplianceResults } from '../interfaces/compliance/results/insurance/gl-insurance-compliance-results';
import { InsuranceComplianceResults } from '../interfaces/compliance/results/insurance/insurance-compliance-results';
import { PLInsuranceComplianceResults } from '../interfaces/compliance/results/insurance/pl-insurance-compliance-results';
import { UMBInsuranceComplianceResults } from '../interfaces/compliance/results/insurance/umb-insurance-compliance-results';
import { WCInsuranceComplianceResults } from '../interfaces/compliance/results/insurance/wc-insurance-compliance-results';
import { InsurancePolicyTypeAbbreviated } from '../interfaces/insurance-policy-type-abbreviation';
import { State } from '../interfaces/state';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class ComplianceService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  saveBusinessComplianceSetting(
    CompanyId: number,
    BusinessComplianceSetting: BusinessComplianceSetting,
    query = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(
              `/api/v2/admin/company/${CompanyId}/compliance/businessComplianceSetting`,
              BusinessComplianceSetting,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveComplianceSetting(
    ComplianceSetting: ComplianceSetting,
    query = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(
              `/api/v2/admin/compliance/setting`,
              ComplianceSetting,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteComplianceSetting(
    ComplianceSettingId: number,
    query = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.delete(
              `/api/v2/admin/compliance/setting/${ComplianceSettingId}`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  overrideComplianceFinding(
    {
      VendorId,
      ClientRegisteredVendorId
    }: { VendorId?: number; ClientRegisteredVendorId?: number },
    {
      ComplianceFinding,
      Note,
      CompanyVendorFiles
    }: {
      ComplianceFinding: ComplianceFinding;
      Note: Note;
      CompanyVendorFiles?: CompanyVendorFile[];
    },
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToVendorObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          // 'Client': () => this.http.post(`/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/${VendorId}/compliance/finding/override`, { ComplianceFinding, Note, CompanyVendorFiles }, { params: query }),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/vendor/${VendorId}/compliance/finding/override`,
              { ComplianceFinding, Note },
              { params: query }
            )
        };

        const userTypeToClientRegisteredVendorObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/${ClientRegisteredVendorId}/compliance/finding/override`,
              { ComplianceFinding, Note, CompanyVendorFiles },
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/clientRegisteredVendor/${ClientRegisteredVendorId}/compliance/finding/override`,
              { ComplianceFinding, Note, CompanyVendorFiles },
              { params: query }
            )
        };

        if (VendorId && userTypeToVendorObservableMap[userType]) {
          return userTypeToVendorObservableMap[userType]();
        }

        if (
          ClientRegisteredVendorId &&
          userTypeToClientRegisteredVendorObservableMap[userType]
        ) {
          return userTypeToClientRegisteredVendorObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  overrideOwnerFinding(
    VendorId: number,
    { OwnerFinding, Note }: { OwnerFinding: OwnerFinding; Note: Note },
    query = {}
  ) {
    return this.http.post<{ OwnerFinding: OwnerFinding; Note: Note }>(
      `/api/v2/admin/vendor/${VendorId}/compliance/ownerFinding/override`,
      { OwnerFinding, Note },
      { params: query }
    );
  }

  getTierLevelForVendor(CompanyId: number, VendorId: number, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${VendorId}/company/${CompanyId}/compliance/getTierLevelForVendor`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${CompanyId}/vendor/${VendorId}/compliance/getTierLevelForVendor`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/vendor/${VendorId}/company/${CompanyId}/compliance/getTierLevelForVendor`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTierLevelForVendorV2(
    CompanyId: number,
    VendorId: number,
    CompanyComplianceTypeId: number,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${VendorId}/company/${CompanyId}/compliance/${CompanyComplianceTypeId}/getTierLevelForVendorV2`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${CompanyId}/vendor/${VendorId}/compliance/${CompanyComplianceTypeId}/getTierLevelForVendorV2`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/vendor/${VendorId}/company/${CompanyId}/compliance/${CompanyComplianceTypeId}/getTierLevelForVendorV2`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  // Instead of the multi-part process of getting Client/Vendor information, formatting data, and testing compliance
  // values, let's retrieve compliance results in a single call to the backend.
  getComplianceResults(CompanyId: number, VendorId: number, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${VendorId}/company/${CompanyId}/compliance/getComplianceResults`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${CompanyId}/vendor/${VendorId}/compliance/getComplianceResults`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/company/${CompanyId}/vendor/${VendorId}/compliance/getComplianceResults`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getBusinessComplianceSetting(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/compliance/businessComplianceSetting/one`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/admin/compliance/businessComplianceSetting/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getBusinessComplianceResults(
    CompanyId: number,
    ClientRegisteredVendorId?: number,
    VendorId?: number,
    query = {}
  ) {
    if (!ClientRegisteredVendorId && !VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot get business compliance results. Missing vendor info.',
        { returnError: true }
      );
    }

    const vendorRoute = ClientRegisteredVendorId
      ? `clientRegisteredVendor/${ClientRegisteredVendorId}`
      : `vendor/${VendorId}`;

    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/${vendorRoute}/company/${CompanyId}/compliance/getBusinessComplianceResults`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${CompanyId}/${vendorRoute}/compliance/getBusinessComplianceResults`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/company/${CompanyId}/${vendorRoute}/compliance/getBusinessComplianceResults`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  // Retrieves the shared states between a Client and Vendor regardless of whether they
  // share a relationship or not.
  getClientVendorSharedStates(CompanyId: number, VendorId: number, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<State[]>;
        } = {
          Vendor: () =>
            this.http.get<State[]>(
              `/api/v2/vendor/${VendorId}/company/${CompanyId}/compliance/clientVendorSharedStates`,
              { params: query }
            ),
          Client: () =>
            this.http.get<State[]>(
              `/api/v2/company/${CompanyId}/vendor/${VendorId}/compliance/clientVendorSharedStates`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<State[]>(
              `/api/v2/company/${CompanyId}/vendor/${VendorId}/compliance/clientVendorSharedStates`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  // This method will accept an object containing the compliance results for the various insurance findings
  // and determine if the Insurance Policy as a whole is in a specific status. (i.e. Passed/Missing/Failed etc.)
  calculateInsurancePolicyResults(
    ComplianceResults: {
      [
        key in
          | keyof GLInsuranceComplianceResults
          | keyof AUTOInsuranceComplianceResults
          | keyof WCInsuranceComplianceResults
          | keyof UMBInsuranceComplianceResults
          | keyof PLInsuranceComplianceResults
      ]: ComplianceResultStatuses;
    } & {
      settings: ComplianceSetting;
    },
    InsurancePolicyType: InsurancePolicyTypeAbbreviated | 'ALL'
  ): InsuranceComplianceResults {
    const PolicyTypeToPolicyResults: {
      GL: (keyof GLInsuranceComplianceResults)[];
      AUTO: (keyof AUTOInsuranceComplianceResults)[];
      WC: (keyof WCInsuranceComplianceResults)[];
      UMB: (keyof UMBInsuranceComplianceResults)[];
      PL: (keyof PLInsuranceComplianceResults)[];
    } = {
      GL: [
        'generalLiability_amBestRating',
        'generalLiability_financialSizeCategory',
        'generalLiability_policyAmountPerOccurance',
        'generalLiability_policyAmountAggregate',
        'generalLiability_productOps',
        'generalLiability_medicalExpenses',
        'generalLiability_damageToRentedPremises',
        'generalLiability_nonOwnedAutoAmount',
        'personalAdvertisementInjury',
        'glIsNamed',
        'glIsAgency',
        'generalLiability_hasAiOngoingOps',
        'generalLiability_hasAiCompOps',
        'generalLiability_hasPnc',
        'generalLiability_hasPolicyDoc',
        'generalLiability_hasDescOps',
        'generalLiability_hasRequiredForms',
        'generalLiability_isContractualLiability',
        'generalLiability_isPerOccurrence',
        'glPerProjLocAgg',
        'glAdditionalInsuredEndorsements'
      ],
      AUTO: [
        'allowNonCommercialPolicy',
        'autoLiability_amBestRating',
        'autoLiability_financialSizeCategory',
        'autoLiability_combinedSingleLimit',
        'autoLiability_propertyDamageLimit',
        'autoLiability_bodilyInjuryPerPerson',
        'autoLiability_bodilyInjuryPerAccident',
        'autoIsNamed',
        'autoIsAgency',
        'autoDescOps',
        'autoHasNonOwnedAuto',
        'autoLiability_hasPolicyDoc',
        'autoLiability_hasDescOps',
        'autoLiability_hasRequiredForms',
        'autoAdditionalInsuredEndorsements'
      ],
      WC: [
        'workersCompensation_amBestRating',
        'workersCompensation_financialSizeCategory',
        'workersCompensation_eachIncidentEmployersLimit',
        'workersCompensation_eachEmployeeMinimum',
        'workersCompensation_eachEmployerMinimum',
        'wkDescOps',
        'wkIsAgency',
        'workersCompensation_hasDescOps',
        'workersCompensation_hasRequiredForms',
        'workersCompensation_officerExcluded'
      ],
      UMB: [
        'umbrellaLiability_amBestRating',
        'umbrellaLiability_financialSizeCategory',
        'umbrellaLiability_policyAmountPerOccurance',
        'umbrellaLiability_policyAmountAggregate',
        'umbIsAgency',
        'umbIsNamed',
        'umbDescOps',
        'umbrellaLiability_hasPolicyDoc',
        'umbrellaLiability_hasDescOps',
        'umbrellaLiability_hasRequiredForms',
        'umbPrimaryNonContributory',
        'umbOccurrenceBased'
      ],
      PL: [
        'errorsAndOmissions_amBestRating',
        'errorsAndOmissions_financialSizeCategory',
        'errorsAndOmissions_policyAmount',
        'errIsNamed',
        'errIsAgency',
        'errDescOps',
        'errorsAndOmissions_hasDescOps'
      ]
    };

    const {
      settings: {
        generalLiability_isRequired,
        autoLiability_isRequired,
        umbrellaLiability_isRequired,
        workersCompensation_isRequired,
        errorsAndOmissions_isRequired
      }
    } = ComplianceResults;

    const PolicyTypeToPolicyRequirement: {
      [key in InsurancePolicyTypeAbbreviated]: boolean;
    } = {
      GL: !!generalLiability_isRequired,
      AUTO: !!autoLiability_isRequired,
      WC: !!workersCompensation_isRequired,
      UMB: !!umbrellaLiability_isRequired,
      PL: !!errorsAndOmissions_isRequired
    };

    const PolicyResults: InsuranceComplianceResults = {};
    const PolicyTypeArray: InsurancePolicyTypeAbbreviated[] =
      InsurancePolicyType === 'ALL'
        ? ['GL', 'AUTO', 'WC', 'UMB', 'PL']
        : [InsurancePolicyType];
    for (let policyType of PolicyTypeArray) {
      const policyResults = PolicyTypeToPolicyResults[policyType];

      let result: ComplianceResultStatuses = 'NOT REQUIRED';
      if (PolicyTypeToPolicyRequirement[policyType]) {
        result = 'PASSED';
        let hasRequiredResult = false;
        for (let pr of policyResults) {
          const findingResult = ComplianceResults[pr];
          if (findingResult !== 'NOT REQUIRED') {
            hasRequiredResult = true;
            result = findingResult;
            if (['FAILED', 'MISSING'].includes(findingResult)) {
              break;
            }
          }
        }
        if (!hasRequiredResult) {
          result = 'NOT REQUIRED';
        }
      }

      Object.assign(PolicyResults, { [policyType]: result });
    }

    return PolicyResults;
  }

  calculateCanadianCompensationFindingAndResult(
    CanadianWorkersCompensation: CanadianWorkersCompensation | null,
    WorkerCompRequired: boolean
  ) {
    const WorkersCompFindingResult = { finding: null, result: null };

    if (!CanadianWorkersCompensation && WorkerCompRequired) {
      return Object.assign(WorkersCompFindingResult, {
        finding: null,
        result: 'MISSING'
      });
    }

    if (CanadianWorkersCompensation) {
      const {
        CAWCNumber,
        CAWCcertNumber,
        CAWCStatus,
        CAWCDate,
        CAWCExempt,
        type
      } = CanadianWorkersCompensation;

      if (!WorkerCompRequired) {
        Object.assign(WorkersCompFindingResult, {
          finding: `${type} NOT REQUIRED`,
          result: 'NOT REQUIRED'
        });
      }

      if (CAWCExempt) {
        Object.assign(WorkersCompFindingResult, {
          finding: `Exempt from ${type} Coverage`,
          result: 'EXEMPT'
        });
      }

      if (!CAWCExempt && (!CAWCDate || !CAWCNumber || !CAWCStatus)) {
        Object.assign(WorkersCompFindingResult, {
          finding: `No ${type} Coverage`,
          result: 'MISSING'
        });
      }

      // We do not set EXPIRED status until the following day after assigned CAWCDate
      if (
        CAWCStatus === 'Eligible' &&
        CAWCDate &&
        new Date(CAWCDate).getTime() < Date.now()
      ) {
        Object.assign(WorkersCompFindingResult, {
          finding: `Expired ${type} Certificate`,
          result: 'EXPIRED'
        });
      }

      if (CAWCStatus === 'Ineligible' || CAWCStatus === 'Preliminary') {
        Object.assign(WorkersCompFindingResult, {
          finding: `${CAWCStatus} ${type} Certificate`,
          result: 'FAILED'
        });
      }

      if (
        CAWCDate &&
        new Date(CAWCDate).getTime() > Date.now() &&
        CAWCStatus === 'Eligible' &&
        (CAWCNumber || CAWCcertNumber) &&
        !CAWCExempt
      ) {
        Object.assign(WorkersCompFindingResult, {
          finding: `${type} Certificate Valid`,
          result: 'PASSED'
        });
      }
    }

    return WorkersCompFindingResult;
  }

  runBusinessVerificationOnVendor(
    CompanyId: number,
    {
      VendorId,
      ClientRegisteredVendorId
    }: { VendorId?: number; ClientRegisteredVendorId?: number },
    query = {}
  ) {
    if (VendorId && ClientRegisteredVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot run compliance for Vendor & Client Registered Vendor simultaneously.',
        { returnError: true }
      );
    }

    const vendorRoute = ClientRegisteredVendorId
      ? `clientRegisteredVendor/${ClientRegisteredVendorId}`
      : `vendor/${VendorId}`;
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/${vendorRoute}/compliance/runBusinessVerificationOnVendor`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/admin/company/${CompanyId}/${vendorRoute}/compliance/runBusinessVerificationOnVendor`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getBusinessVerificationApiErrorsNotResolvedCount(
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/compliance/getBusinessVerificationApiErrorsNotResolvedCount`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              '/api/v2/admin/compliance/getBusinessVerificationApiErrorsNotResolvedCount',
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  runVerificationOnOwner(
    {
      VendorId,
      OwnerId,
      CompanyId
    }: { VendorId: number; OwnerId: number; CompanyId: number | undefined },
    query = {}
  ) {
    return this.http.post<[Owner, any]>(
      `/api/v2/admin/compliance/runVerificationOnOwner`,
      { VendorId, OwnerId, CompanyId },
      { params: query }
    );
  }

  wsibLookup(
    params = {},
    query: { CompanyVendorId: number; CompanyId: number; VendorId: number }
  ) {
    return this.http.post<{
      wsibResponseData: any;
      CompanyVendorFile?: CompanyVendorFile;
    }>(`/api/v2/admin/compliance/wsibLookup`, params, { params: query });
  }
}
